import type { PairDimensionId, RecommendationDoc } from '@astrally/shared';
import type { ScoredCandidate } from './candidates.js';

/**
 * Deterministic plain-language rationale for a recommendation. The LLM only
 * ever EXPLAINS these already-calculated results — it never invents grades.
 */

const DIMENSION_LABEL: Record<PairDimensionId, string> = {
  intentionFit: 'intention fit',
  communicationFit: 'communication fit',
  sharedInterests: 'shared interests',
  lifeStageFit: 'life-stage fit',
  geoPracticality: 'location practicality',
  jyotishCompat: 'Jyotish compatibility',
  westernCompat: 'Western-astrology compatibility',
  reliability: 'reliability',
};

export function buildRationale(sc: ScoredCandidate): string[] {
  const { pair } = sc;
  const rationale: string[] = [];

  if (pair.extras.sharedIntents.length > 0) {
    rationale.push(
      `You are both open to ${pair.extras.sharedIntents.join(', ').replace(/-/g, ' ')}.`,
    );
  }
  if (pair.extras.sharedInterestList.length > 0) {
    rationale.push(`Shared interests: ${pair.extras.sharedInterestList.slice(0, 5).join(', ')}.`);
  }

  const strong = (Object.keys(pair.dimensions) as PairDimensionId[])
    .filter((k) => pair.dimensions[k].grade === 'A' || pair.dimensions[k].grade === 'B')
    .sort((a, b) => pair.dimensions[b].score - pair.dimensions[a].score)
    .slice(0, 3);
  for (const k of strong) {
    rationale.push(
      `${capitalize(DIMENSION_LABEL[k])} grade ${pair.dimensions[k].grade}.`,
    );
  }
  return rationale;
}

export function gradesSnapshot(
  sc: ScoredCandidate,
): RecommendationDoc['grades'] {
  const grades = {} as RecommendationDoc['grades'];
  for (const [k, v] of Object.entries(sc.pair.dimensions)) {
    grades[k as PairDimensionId] = v.grade;
  }
  grades.overall = sc.pair.overall.grade;
  return grades;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
